import {
  Autocomplete,
  FormGroup,
  Paper,
  FormControlLabel,
  Switch,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Checkbox,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useGetCompanyList } from "app/hooks/company/useGetCompanyList";
import { EmploymentTypes, LocationOptions } from "app/views/jobs/Constants";
import { useCallback } from "react";

function FilterWrapper({
  empType,
  locType,
  salary,
  exp,
  city,
  searchTerm,
  company,
  handleCompanyChange,
  handleJobTypeChange,
  handleSalaryChange,
  handleLocationChange,
  handleExpChange,
  handleCityChange,
  handleSearch,
  handleClearSearch,
  handleClearAllFilters
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Use the passed handleClearAllFilters function if available, otherwise fall back to local implementation
  const handleClearAll = useCallback(() => {
    if (handleClearAllFilters) {
      handleClearAllFilters();
    } else {
      // Fallback to local implementation
      handleJobTypeChange([]);
      handleSalaryChange([0, 30]);
      handleLocationChange([]);
      handleExpChange("all");
      handleCityChange("");
      handleClearSearch();
    }
  }, [
    handleClearAllFilters,
    handleJobTypeChange,
    handleSalaryChange,
    handleLocationChange,
    handleExpChange,
    handleCityChange,
    handleClearSearch
  ]);

  // Ensure arrays for multiple-select filters
  const empTypeValues = Array.isArray(empType) ? empType : [];
  const locTypeValues = Array.isArray(locType) ? locType : [];

  const { data: companies, isLoading: isFetchingCompanies } = useGetCompanyList({
    isEnable: true,
    staleTime: Infinity
  });
  const companyOptions = companies
    ? companies.map((comp) => ({ id: comp.jp_id, label: comp.jp_company_name }))
    : [];

  // Experience options
  const experienceOptions = [
    { value: "all", label: "All Experience Levels" },
    { value: "0-1", label: "0-1 years" },
    { value: "1-3", label: "1-3 years" },
    { value: "3-5", label: "3-5 years" },
    { value: "5-7", label: "5-7 years" },
    { value: "7+", label: "7+ years" }
  ];

  // Salary options
  const salaryOptions = [
    { value: "all", label: "All Salary Ranges" },
    { value: "0-10", label: "0-10 lakhs" },
    { value: "10-20", label: "10-20 lakhs" },
    { value: "20-30", label: "20-30 lakhs" },
    { value: "30-40", label: "30-40 lakhs" },
    { value: "40-100", label: "40+ lakhs" }
  ];

  const selectedCompanyOption = companyOptions.find((opt) => opt.id === company) || null;

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 1.5 },
        borderRadius: "8px"
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ display: { xs: "hidden", md: "block" }, mb: 2 }}
      >
        Filters
      </Typography>

      {/* Filters Grid */}
      <Grid container spacing={2}>
        {/* Job Type Filter */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="medium" mb={0.5}>
            Job Type
          </Typography>
          <FormGroup>
            {EmploymentTypes.map((type) => {
              const checked = empTypeValues.includes(type.label);
              return (
                <Paper
                  key={type.id || type.label}
                  elevation={checked ? 3 : 0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    borderRadius: 2,
                    px: 1,
                    bgcolor: checked ? "#3f51b5" : "#f7f7fa",
                    color: checked ? "#fff" : "#222",
                    border: checked ? "1.5px solid #3f51b5" : "1.5px solid #e0e0e0",
                    transition: "all 0.2s"
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            handleJobTypeChange(empTypeValues.filter((v) => v !== type.label));
                          } else {
                            handleJobTypeChange([...empTypeValues, type.label]);
                          }
                        }}
                        color="primary"
                      />
                    }
                    labelPlacement="start"
                    label={type.label}
                    sx={{
                      ml: 1,
                      width: "100%",
                      justifyContent: "space-between",
                      color: checked ? "#fff" : "#222",
                      fontWeight: checked ? 600 : 400
                    }}
                  />
                </Paper>
              );
            })}
          </FormGroup>
        </Grid>

        {/* Salary Range Filter */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="medium" mb={0.5}>
            Filter by Salary Range
          </Typography>
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <Select
              value={
                Array.isArray(salary) && salary.length === 2 ? `${salary[0]}-${salary[1]}` : "0-10"
              }
              onChange={(e) => {
                if (e.target.value === "all") {
                  handleSalaryChange([0, 10]);
                } else {
                  const [min, max] = e.target.value.split("-").map(Number);
                  handleSalaryChange([min, max]);
                }
              }}
              sx={{
                borderRadius: "4px",
                height: { xs: "36px", md: "40px" },
                bgcolor: "white"
              }}
              renderValue={(selected) =>
                selected === "all"
                  ? "All Salary Ranges"
                  : `${selected.split("-")[0]}-${selected.split("-")[1]} lakhs`
              }
            >
              {salaryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Location Type Filter as Checkboxes */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="medium" mb={0.5}>
            Filter by Location Type
          </Typography>
          <FormGroup>
            {LocationOptions.map((loc) => (
              <FormControlLabel
                key={loc.id || loc.label}
                control={
                  <Checkbox
                    checked={locTypeValues.includes(loc.label)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleLocationChange([...locTypeValues, loc.label]);
                      } else {
                        handleLocationChange(locTypeValues.filter((v) => v !== loc.label));
                      }
                    }}
                    color="primary"
                  />
                }
                label={loc.label}
              />
            ))}
          </FormGroup>
        </Grid>

        {/* Experience Filter as Checkboxes */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="medium" mb={0.5}>
            Filter by Experience
          </Typography>
          <FormGroup>
            {experienceOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={
                      Array.isArray(exp)
                        ? exp.includes(option.value)
                        : exp === option.value ||
                          (option.value === "all" && (!exp || exp === "all"))
                    }
                    onChange={(e) => {
                      if (Array.isArray(exp)) {
                        if (e.target.checked) {
                          handleExpChange([...exp, option.value]);
                        } else {
                          handleExpChange(exp.filter((v) => v !== option.value));
                        }
                      } else {
                        if (e.target.checked) {
                          handleExpChange([option.value]);
                        } else {
                          handleExpChange([]);
                        }
                      }
                    }}
                    color="primary"
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="medium" mb={0.5}>
            Filter by Company
          </Typography>
          <Autocomplete
            value={selectedCompanyOption}
            onChange={(event, newValue) => {
              handleCompanyChange(event, newValue);
            }}
            options={companyOptions}
            size="small"
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ background: "white" }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            loading={isFetchingCompanies}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetchingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="text" onClick={handleClearAll}>
          Clear All Filters
        </Button>
      </Box>
    </Box>
  );
}

export default FilterWrapper;
